import { type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { Button } from 'Button';
import { AssessmentScoreArc } from './AssessmentScoreArc';
import { LessonProgressIcon } from './LessonProgressIcon';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    The progress badge and launch button on desktop; a coloured 30px bar on mobile.
    The row and this button both launch. The row is the only control on a phone, and hiding a focusable button
    from assistive technology is a defect. Known limitation: a screen reader announces 2 controls per lesson.
    Button appends its own size classes inside twMerge, so the height needs !h-11.
*/

type Props = {
    isComingSoon: boolean;
    isSignedIn: boolean;
    isAssessment: boolean;
    isAssessmentOrTest: boolean;
    isInProgress: boolean;
    isComplete: boolean;
    canLaunch: boolean;
    isClickable: boolean;
    showAssessmentArc: boolean;
    bestScore: number | undefined;
    launchTextKey: string;
    onLaunch: () => void;
};

/*
    Below sm the column is a 30px bar coloured by the lesson's state, with a white chevron on it. From sm
    up it becomes the 182px panel with the badge and the launch button, and the bar's colour gives way to
    slate-grey-50. The sm background wins without an important, because Tailwind emits responsive variants
    after the base utilities.
*/
const columnClasses =
    'relative flex w-7.5 flex-col items-center justify-center sm:w-[182px] sm:bg-slate-grey-50 sm:px-3.5';

// Completed uses the brand primary (coral on Quantic). The other state colours are not brand colours. Turquoise wins over progress.
function mobileBarColorClass({
    isComingSoon,
    isAssessmentOrTest,
    isInProgress,
    isComplete,
}: Pick<Props, 'isComingSoon' | 'isAssessmentOrTest' | 'isInProgress' | 'isComplete'>) {
    if (isComingSoon) return 'bg-beige-dark';
    if (isAssessmentOrTest) return 'bg-turquoise';
    if (isComplete) return 'bg-primary';
    if (isInProgress) return 'bg-purple';
    return 'bg-green';
}

// Button has no turquoise, so an exam or SMARTcase overrides it. Turquoise wins over progress.
function launchButtonColor({ isInProgress, isComplete }: Pick<Props, 'isInProgress' | 'isComplete'>) {
    if (isComplete) return 'primary';
    if (isInProgress) return 'purple';
    return 'green';
}

export const LessonProgressColumn: FC<Props> = ({
    isComingSoon,
    isSignedIn,
    isAssessment,
    isAssessmentOrTest,
    isInProgress,
    isComplete,
    canLaunch,
    isClickable,
    showAssessmentArc,
    bestScore,
    launchTextKey,
    onLaunch,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    if (isComingSoon) {
        return (
            <div
                className={`${columnClasses} ${mobileBarColorClass({
                    isComingSoon,
                    isAssessmentOrTest,
                    isInProgress,
                    isComplete,
                })}`}
            >
                <span className="text-center text-md font-semibold uppercase text-slate-grey max-sm:hidden">
                    <Trans t={t} i18nKey="coming_soon_block" components={{ lineBreak: <br /> }} />
                </span>
                <FontAwesomeIcon icon={faClock} className="text-lg text-white sm:hidden" />
            </div>
        );
    }

    return (
        <div
            className={`${columnClasses} gap-2.5 sm:pb-12.5 ${mobileBarColorClass({
                isComingSoon,
                isAssessmentOrTest,
                isInProgress,
                isComplete,
            })}`}
        >
            {/* On mobile, a finished lesson shows the 30px badge and no chevron. An unfinished lesson shows the chevron. */}
            {isComplete ? (
                <LessonProgressIcon
                    isAssessmentOrTest={isAssessmentOrTest}
                    isInProgress={isInProgress}
                    isComplete={isComplete}
                    isLocked={!canLaunch}
                    className="h-7.5 w-7.5 sm:hidden"
                />
            ) : (
                !isAssessment && (
                    <FontAwesomeIcon
                        icon={isSignedIn ? faChevronRight : faLock}
                        className="text-lg text-white sm:hidden"
                    />
                )
            )}

            {showAssessmentArc ? (
                /* 72px is what the legacy container gave the arc: 2*30 + 8 stroke + 4 padding. Without an
                   explicit size the svg fills its parent and renders far larger. */
                <AssessmentScoreArc
                    radius={30}
                    scoreFontSize={14}
                    score={bestScore}
                    className="h-18 w-18 max-sm:hidden"
                />
            ) : (
                <LessonProgressIcon
                    isAssessmentOrTest={isAssessmentOrTest}
                    isInProgress={isInProgress}
                    isComplete={isComplete}
                    isLocked={!canLaunch}
                    className="max-sm:hidden"
                />
            )}

            {/* A started but unfinished assessment shows no arc, so the old markup added a second
                mobile chevron here. Kept for parity. */}
            {isAssessment && isInProgress && !isComplete && (
                <FontAwesomeIcon icon={faChevronRight} className="text-lg text-white sm:hidden" />
            )}

            <Button
                color={launchButtonColor({ isInProgress, isComplete })}
                isDisabled={!isClickable}
                onPress={onLaunch}
                className={`absolute inset-x-0 bottom-0 !h-11 w-full rounded-none max-sm:hidden ${
                    isAssessmentOrTest ? '!bg-turquoise' : ''
                }`}
            >
                {t(launchTextKey)}
            </Button>
        </div>
    );
};
